/**
 * Projects Page
 * List all projects with API keys and integration instructions
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ProtectedRoute from '~/components/ProtectedRoute';
import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, Copy } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

interface Project {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  admin_api_key: string; // Masked by default
  rate_limit_per_hour: number;
  created_at: number;
  updated_at: number;
}

export default function Projects() {
  return (
    <ProtectedRoute>
      <ProjectsContent />
    </ProtectedRoute>
  );
}

interface Organization {
  id: string;
  name: string;
}

function ProjectsContent() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(new Set());
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null);

  useEffect(() => {
    fetchProjects();
    fetchOrganizations();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/projects`, {
        credentials: 'include', // Include cookies for session
      });

      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }

      const data = await response.json();
      setProjects(data.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/organizations`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }

      const data = await response.json();
      setOrganizations(data.data || []);
    } catch (err: any) {
      console.error('Failed to load organizations:', err);
    }
  };

  const createProject = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError('');

    try {
      // Use first organization if available
      const orgId = organizations[0]?.id || projects[0]?.organization_id;

      if (!orgId) {
        throw new Error('No organization found');
      }

      const response = await fetch(`${API_URL}/api/v1/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          organization_id: orgId,
          name: newProjectName,
          description: newProjectDescription || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to create project');
      }

      const data = await response.json();

      // Store the full API key to show to user
      setCreatedApiKey(data.data.admin_api_key_full);

      // Add new project to list (with masked key)
      setProjects([...projects, {
        ...data.data,
        admin_api_key: data.data.admin_api_key, // Already masked from server
      }]);

      // Reset form
      setNewProjectName('');
      setNewProjectDescription('');
      setShowCreateForm(false);
    } catch (err: any) {
      setError(err.message || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const copyApiKey = async (apiKey: string) => {
    try {
      await navigator.clipboard.writeText(apiKey);
      toast.success('API key copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy API key');
    }
  };

  const copyWidgetCode = async (apiKey: string) => {
    const widgetCode = `<script src="https://cdn.snaptics.cc/widget.js" data-api-key="${apiKey}"></script>`;
    try {
      await navigator.clipboard.writeText(widgetCode);
      toast.success('Widget code copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy widget code');
    }
  };

  const toggleApiKeyVisibility = async (projectId: string) => {
    if (revealedKeys.has(projectId)) {
      // Hide the key - mask it again
      setProjects(projects.map(p => {
        if (p.id === projectId) {
          const key = p.admin_api_key;
          const maskedKey = `${key.slice(0, 7)}...${key.slice(-4)}`;
          return { ...p, admin_api_key: maskedKey };
        }
        return p;
      }));

      // Remove from revealed set
      setRevealedKeys(prev => {
        const newSet = new Set(prev);
        newSet.delete(projectId);
        return newSet;
      });
    } else {
      // Reveal the key - fetch full API key from server
      try {
        const response = await fetch(`${API_URL}/api/v1/projects/${projectId}/api-key`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch API key');
        }

        const data = await response.json();
        const fullKey = data.data.admin_api_key;

        // Update the project with full key
        setProjects(projects.map(p =>
          p.id === projectId ? { ...p, admin_api_key: fullKey } : p
        ));

        // Mark as revealed
        setRevealedKeys(new Set([...revealedKeys, projectId]));
      } catch (err: any) {
        toast.error(err.message || 'Failed to reveal API key');
      }
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-8">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
          <p className="mt-4 text-sm text-gray-600">Loading projects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-8">
        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-md">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Projects</h1>
          <p className="text-gray-600 mt-1">
            Manage your feedback collection projects
          </p>
        </div>
        <Button onClick={() => setShowCreateForm(true)}>
          Create Project
        </Button>
      </div>

      {/* Create Project Form */}
      {showCreateForm && (
        <Card className="p-6 space-y-4">
          <h2 className="text-xl font-semibold">Create New Project</h2>
          <form onSubmit={createProject} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Project Name *
              </label>
              <input
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                required
                maxLength={100}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="My Awesome Project"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Description (optional)
              </label>
              <textarea
                value={newProjectDescription}
                onChange={(e) => setNewProjectDescription(e.target.value)}
                maxLength={500}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="A brief description of your project"
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={creating || !newProjectName}>
                {creating ? 'Creating...' : 'Create'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewProjectName('');
                  setNewProjectDescription('');
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Show created API key */}
      {createdApiKey && (
        <Card className="p-6 bg-green-50 border-green-200 space-y-3">
          <h3 className="font-semibold text-green-900">Project Created Successfully!</h3>
          <p className="text-sm text-green-800">
            Save this API key - it will only be shown once:
          </p>
          <div className="flex gap-2">
            <code className="flex-1 text-xs bg-white p-3 rounded font-mono break-all border border-green-200">
              {createdApiKey}
            </code>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                copyApiKey(createdApiKey);
                setCreatedApiKey(null);
              }}
            >
              Copy
            </Button>
          </div>
        </Card>
      )}

      {projects.length === 0 && !showCreateForm ? (
        <Card className="p-8 text-center space-y-4">
          <p className="text-gray-600">No projects yet. Create one to get started!</p>
          <Button onClick={() => setShowCreateForm(true)}>Create Project</Button>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => {
            const isRevealed = revealedKeys.has(project.id);
            const apiKey = project.admin_api_key;

            return (
              <Card key={project.id} className="p-6 space-y-4">
                <div>
                  <h3 className="text-xl font-semibold">{project.name}</h3>
                  {project.description && (
                    <p className="text-sm text-gray-600 mt-1">{project.description}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <span className="text-sm font-medium">API Key</span>
                  <div className="relative">
                    <code className="block text-xs bg-gray-100 p-3 pr-20 rounded font-mono break-all">
                      {apiKey}
                    </code>
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => toggleApiKeyVisibility(project.id)}
                        title={isRevealed ? 'Hide API key' : 'Show API key'}
                      >
                        {isRevealed ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={async () => {
                          // Fetch full API key if not revealed
                          let keyToCopy = apiKey;
                          if (!isRevealed) {
                            try {
                              const response = await fetch(`${API_URL}/api/v1/projects/${project.id}/api-key`, {
                                credentials: 'include',
                              });
                              if (response.ok) {
                                const data = await response.json();
                                keyToCopy = data.data.admin_api_key;
                              }
                            } catch (err) {
                              toast.error('Failed to fetch API key');
                              return;
                            }
                          }
                          copyApiKey(keyToCopy);
                        }}
                        title="Copy API key"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t space-y-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={async () => {
                      // Fetch full API key if not revealed
                      let keyToCopy = apiKey;
                      if (!isRevealed) {
                        try {
                          const response = await fetch(`${API_URL}/api/v1/projects/${project.id}/api-key`, {
                            credentials: 'include',
                          });
                          if (response.ok) {
                            const data = await response.json();
                            keyToCopy = data.data.admin_api_key;
                          }
                        } catch (err) {
                          toast.error('Failed to fetch API key');
                          return;
                        }
                      }
                      copyWidgetCode(keyToCopy);
                    }}
                  >
                    Copy Widget Code
                  </Button>
                  <Link to={`/?project=${project.id}`} className="block">
                    <Button variant="outline" size="sm" className="w-full">
                      View Feedback
                    </Button>
                  </Link>
                </div>

                <div className="text-xs text-gray-500 pt-2 border-t">
                  <p>Rate limit: {project.rate_limit_per_hour}/hour</p>
                  <p>Created: {new Date(project.created_at * 1000).toLocaleDateString()}</p>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Card className="p-6 bg-blue-50 border-blue-200">
        <h3 className="font-semibold mb-2">Getting Started</h3>
        <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
          <li>Click "Copy Widget Code" on your project above</li>
          <li>Paste it in your website's HTML, just before the closing &lt;/body&gt; tag</li>
          <li>Users can now submit feedback with screenshots and videos!</li>
          <li>View feedback by clicking "View Feedback" on your project</li>
        </ol>
      </Card>
    </div>
  );
}
